import type { Gender } from 'src/constants/user';
import type {
  CanEditRole,
  CanEditRoleUpstream,
  EmailUserLanguage,
  Role,
  RoleUpstream,
  SharableUser,
  SharableUserUpstream,
  User,
  UserAdminListUser,
  UserAdminListUserUpstream,
  UserUpstream,
} from 'src/types/user';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';

export const mapRoleToProps = (data: RoleUpstream): Role => ({
  id: data.id,
  slug: data.slug,
  name: data.name,
  displayName: data.display_name,
  canEditRoles: mapCanEditRolesToProps(data.can_edit_roles),
  canBeAdmin: !!data.can_be_admin,
  canEditSurveySettings: !!data.can_edit_survey_settings,
});

export const mapCanEditRolesToProps = (
  data: readonly CanEditRoleUpstream[],
): CanEditRole[] =>
  Array.isArray(data)
    ? data.map(
        (role) =>
          ({
            id: role.id,
            slug: role.slug,
            name: role.name,
            canBeAdmin: role.can_be_admin,
            displayName: role.display_name,
          }) as CanEditRole,
      )
    : ([] as CanEditRole[]);

export const mapUserAdminListUserToProps = (
  data: UserAdminListUserUpstream,
): UserAdminListUser => ({
  business_unit: data.business_unit,
  email: data.email,
  role: data.role ? mapRoleToProps(data.role) : null,
  first_name: data.first_name,
  id: data.id,
  last_name: data.last_name,
});

export const mapUserToProps = (data: UserUpstream): User => ({
  business_unit: data.business_unit,
  email: data.email,
  role: data.role ? mapRoleToProps(data.role) : null,
  first_name: data.first_name,
  id: data.id,
  last_name: data.last_name,
  gender: data.gender as Gender,
  is_superuser: data.is_superuser as boolean,
  language: data.language as EmailUserLanguage,
  surveyPermissions: data.survey_permissions ?? [],
  pro_terms_accepted: data.pro_terms_accepted as string,
  guided_tour_completed_modules: data.guided_tour_completed_modules as number[],
  session: data.session,
  availableSurveyCount: data.available_survey_count,
  isFrontendAdmin: data.is_frontend_admin ?? false,
});

export const mapSharableUserToProps = (
  data: SharableUserUpstream,
): SharableUser => ({
  business_unit: data.business_unit,
  email: data.email,
  first_name: data.first_name,
  first_org_unit_id: data.first_org_unit_id,
  first_org_unit_name: data.first_org_unit_name,
  id: data.id,
  last_name: data.last_name,
  role: data.role ? mapRoleToProps(data.role) : null,
});

export const fetchUpdateSurveySession = (surveyId?: number): Promise<User> => {
  if (Number.isNaN(surveyId)) return Promise.reject();
  return HttpService.post(apiPaths.USER.SWITCH_SURVEY, { survey: surveyId })
    .then(({ data }) => Promise.resolve(mapUserToProps(data)))
    .catch(({ response: { data } }) => Promise.reject(data));
};

/**
 * Converts a 'CanEditRole' to a 'Role' by appending an empty list for the 'canEditRoles'.
 * This can be necessary if a role from the list of roles an admin can edit gets applied to a user.
 */
export const convertCanEditRoleToRole = (canEditRole: CanEditRole): Role => ({
  ...canEditRole,
  canEditSurveySettings: false,
  canEditRoles: [],
  displayName: canEditRole.name,
});
